import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { Users, UserPlus, Mail, Trash2, Settings2, Ban, RotateCcw, Network, Table2 } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'react-hot-toast';
import { EMPLOYER_PERMISSION_OPTIONS, EMPLOYER_ROLE_PRESETS, EmployerTeamRole } from '../config/employerPermissions';
import type { EmployerLocation, EmployerDepartment } from '../components/EmployerOrgStructuur';
import { ToegangsBoom, TeamMember, Toegangsniveau, TOEGANGSNIVEAUS, teamStatus, rolLabel, STATUS_KLASSEN } from '../components/ToegangsBoom';

type Weergave = 'boom' | 'tabel';

/** Wijzigvenster: functie, rol, toegangsniveau en plek in de organisatieboom ('' = universeel). */
interface Wijziging {
  member: TeamMember;
  functietitel: string;
  team_role: EmployerTeamRole;
  toegangsniveau: Toegangsniveau;
  location_id: string;
  department_id: string;
}

type BeheerderNiveau = 'hoofdgebruiker' | 'global_admin' | 'vestigingsmanager';

interface TeamOverzicht {
  members: TeamMember[];
  beheerder: { niveau: BeheerderNiveau; location_id: string | null; user_id: string } | null;
  organisatie: { naam: string | null; hoofdgebruiker: string | null } | null;
}

/** Welke toegangsniveaus een beheerder mag toekennen (anti-sabotage, gelijk aan de backend). */
const toekenbareNiveaus = (b: BeheerderNiveau | undefined): Toegangsniveau[] =>
  b === 'hoofdgebruiker' ? [...TOEGANGSNIVEAUS]
    : b === 'global_admin' ? ['vestigingsmanager', 'afdelingschef']
      : ['afdelingschef'];

/** Toegangsniveau en scope horen bij elkaar: Global Admin zonder plek, Vestigingsmanager zonder afdeling. */
function metNiveau<T extends { toegangsniveau: Toegangsniveau; location_id: string; department_id: string }>(
  p: T,
  niveau: Toegangsniveau
): T {
  if (niveau === 'global_admin') return { ...p, toegangsniveau: niveau, location_id: '', department_id: '' };
  if (niveau === 'vestigingsmanager') return { ...p, toegangsniveau: niveau, department_id: '' };
  return { ...p, toegangsniveau: niveau };
}

const selectKlasse = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

export const EmployerTeamPage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('team.title'), t('team.subtitle'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const managerPreset = EMPLOYER_ROLE_PRESETS.find((p) => p.role === 'manager')!;
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    functietitel: '',
    team_role: 'manager' as EmployerTeamRole,
    toegangsniveau: 'afdelingschef' as Toegangsniveau,
    permissions: [...managerPreset.permissions],
    location_id: '', // '' = Alle Vestigingen (universele scope)
    department_id: '', // '' = Alle Afdelingen
  });
  const [wijziging, setWijziging] = useState<Wijziging | null>(null);
  const [weergave, setWeergave] = useState<Weergave>('boom');
  // Filterbalk totaaloverzicht ('' = alles, 'geen' = universeel/niet gekoppeld)
  const [filter, setFilter] = useState({ location_id: '', department_id: '', toegangsniveau: '' });

  const magToegangsbeheer =
    user?.role === 'opdrachtgever' ||
    user?.employer_toegangsniveau === 'global_admin' ||
    user?.employer_toegangsniveau === 'vestigingsmanager';

  const { data: overzicht, isLoading } = useQuery({
    queryKey: ['employer-team'],
    queryFn: async (): Promise<TeamOverzicht> => {
      const res = await api.get('/employer/team');
      return {
        members: (res.data.data || []) as TeamMember[],
        beheerder: res.data.beheerder ?? null,
        organisatie: res.data.organisatie ?? null,
      };
    },
    enabled: !!user && magToegangsbeheer
  });
  const members = overzicht?.members ?? [];
  const beheerder = overzicht?.beheerder ?? null;
  const isHoofdgebruiker = beheerder?.niveau === 'hoofdgebruiker';
  // Een Vestigingsmanager werkt uitsluitend binnen de eigen vestiging
  const eigenVestiging = beheerder?.niveau === 'vestigingsmanager' ? beheerder.location_id : null;
  const niveauOpties = toekenbareNiveaus(beheerder?.niveau);

  // Bij openen van modal: reset formulier op manager-preset
  useEffect(() => {
    if (showInviteModal) {
      setInviteForm({
        email: '',
        first_name: '',
        last_name: '',
        functietitel: '',
        team_role: 'manager',
        toegangsniveau: 'afdelingschef',
        permissions: [...managerPreset.permissions],
        location_id: eigenVestiging || '',
        department_id: '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij openen modal
  }, [showInviteModal]);

  const applyRolePreset = (role: EmployerTeamRole) => {
    const preset = EMPLOYER_ROLE_PRESETS.find((p) => p.role === role)!;
    setInviteForm((p) => ({ ...p, team_role: role, permissions: [...preset.permissions] }));
  };

  // Toegangs-Scope: vestigingen + afdelingen uit Tab 1 (Organisatieprofiel)
  const { data: alleLocations = [] } = useQuery({
    queryKey: ['employer-locations'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
    enabled: !!user && magToegangsbeheer
  });
  const { data: alleDepartments = [] } = useQuery({
    queryKey: ['employer-departments'],
    queryFn: async () => (await api.get('/employer/departments')).data?.data as EmployerDepartment[],
    enabled: !!user && magToegangsbeheer
  });
  const locations = eigenVestiging ? alleLocations.filter((l) => l.id === eigenVestiging) : alleLocations;
  const departments = eigenVestiging
    ? alleDepartments.filter((d) => !d.location_id || d.location_id === eigenVestiging)
    : alleDepartments;

  // Hybride matrix-koppeling: bij een gekozen vestiging alleen universele
  // afdelingen + de afdelingen die exclusief bij die vestiging horen
  const selectableDepartments = departments.filter(
    (d) => !inviteForm.location_id || !d.location_id || d.location_id === inviteForm.location_id
  );

  const wijzigingDepartments = departments.filter(
    (d) => !wijziging?.location_id || !d.location_id || d.location_id === wijziging.location_id
  );

  const filterDepartments = departments.filter(
    (d) => !filter.location_id || filter.location_id === 'geen' || !d.location_id || d.location_id === filter.location_id
  );

  const matchFilter = (waarde: string | null, gekozen: string) =>
    !gekozen || (gekozen === 'geen' ? !waarde : waarde === gekozen);

  const gefilterd = members.filter(
    (m) =>
      matchFilter(m.location_id, filter.location_id) &&
      matchFilter(m.department_id, filter.department_id) &&
      (!filter.toegangsniveau || m.toegangsniveau === filter.toegangsniveau)
  );

  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; first_name: string; last_name: string; functietitel: string; team_role: EmployerTeamRole; toegangsniveau: Toegangsniveau; permissions: string[]; location_id: string | null; department_id: string | null }) => {
      const res = await api.post('/employer/team/invite', data);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('team.inviteSent'));
      setShowInviteModal(false);
      setInviteForm({
        email: '',
        first_name: '',
        last_name: '',
        functietitel: '',
        team_role: 'manager',
        toegangsniveau: 'afdelingschef',
        permissions: [...managerPreset.permissions],
        location_id: eigenVestiging || '',
        department_id: '',
      });
      queryClient.invalidateQueries({ queryKey: ['employer-team'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('team.inviteError'));
    }
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => (await api.delete(`/employer/team/${userId}`)).data,
    onSuccess: (res) => {
      const overgedragen = Number(res?.data?.overgedragen_trajecten || 0);
      toast.success(overgedragen > 0 ? t('team.memberRemovedOvergedragen', { count: overgedragen }) : t('team.memberRemoved'));
      queryClient.invalidateQueries({ queryKey: ['employer-team'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('team.removeError'));
    }
  });

  // Wijzigvenster én slepen in de boom gaan via dezelfde PATCH
  const wijzigMutation = useMutation({
    mutationFn: async ({ userId, ...data }: {
      userId: string;
      functietitel?: string | null;
      team_role?: EmployerTeamRole;
      toegangsniveau?: Toegangsniveau;
      permissions?: string[];
      location_id: string | null;
      department_id: string | null;
    }) => (await api.patch(`/employer/team/${userId}`, data)).data,
    onSuccess: () => {
      toast.success(t('team.wijzigOpgeslagen'));
      setWijziging(null);
      queryClient.invalidateQueries({ queryKey: ['employer-team'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('team.wijzigFout'));
    }
  });

  const statusMutation = useMutation({
    mutationFn: async (data: { userId: string; is_active: boolean }) =>
      (await api.patch(`/employer/team/${data.userId}/status`, { is_active: data.is_active })).data,
    onSuccess: (_res, data) => {
      toast.success(data.is_active ? t('team.heractiveerd') : t('team.ingetrokken'));
      queryClient.invalidateQueries({ queryKey: ['employer-team'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('team.wijzigFout'));
    }
  });

  const openWijziging = (m: TeamMember) =>
    setWijziging({
      member: m,
      functietitel: m.functietitel || '',
      team_role: (EMPLOYER_ROLE_PRESETS.some((p) => p.role === m.team_role) ? m.team_role : 'admin') as EmployerTeamRole,
      toegangsniveau: m.toegangsniveau,
      location_id: m.location_id || '',
      department_id: m.department_id || '',
    });

  const handleWijzigingSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!wijziging) return;
    if (wijziging.toegangsniveau === 'vestigingsmanager' && !wijziging.location_id) {
      toast.error(t('team.niveauVestigingVerplicht'));
      return;
    }
    const rolGewijzigd = wijziging.team_role !== wijziging.member.team_role;
    wijzigMutation.mutate({
      userId: wijziging.member.user_id,
      functietitel: wijziging.functietitel.trim() || null,
      // Bij een rolwissel krijgt het lid de menurechten van de nieuwe rol
      ...(rolGewijzigd && {
        team_role: wijziging.team_role,
        permissions: EMPLOYER_ROLE_PRESETS.find((p) => p.role === wijziging.team_role)!.permissions,
      }),
      toegangsniveau: wijziging.toegangsniveau,
      location_id: wijziging.location_id || null,
      department_id: wijziging.department_id || null,
    });
  };

  const verplaats = (m: TeamMember, plek: { location_id: string | null; department_id: string | null }) =>
    wijzigMutation.mutate({ userId: m.user_id, ...plek });

  const wisselToegang = (m: TeamMember) => {
    const naam = `${m.first_name} ${m.last_name}`.trim();
    if (m.is_active && !window.confirm(t('team.intrekkenConfirm', { name: naam }))) return;
    statusMutation.mutate({ userId: m.user_id, is_active: !m.is_active });
  };

  const verwijder = (m: TeamMember) => {
    if (window.confirm(t('team.removeConfirm', { name: `${m.first_name} ${m.last_name}` }))) {
      removeMutation.mutate(m.user_id);
    }
  };

  const togglePermission = (key: string) => {
    setInviteForm((p) => ({
      ...p,
      permissions: p.permissions.includes(key)
        ? p.permissions.filter((k) => k !== key)
        : [...p.permissions, key],
    }));
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      toast.error(t('team.fillAllFields'));
      return;
    }
    if (inviteForm.toegangsniveau === 'vestigingsmanager' && !inviteForm.location_id) {
      toast.error(t('team.niveauVestigingVerplicht'));
      return;
    }
    const permissions = inviteForm.team_role === 'admin'
      ? inviteForm.permissions
      : inviteForm.permissions.filter((k) => k !== 'facturen' && k !== 'toegang_cockpit');
    inviteMutation.mutate({
      ...inviteForm,
      permissions,
      location_id: inviteForm.location_id || null,
      department_id: inviteForm.department_id || null,
    });
  };

  if (!user || !magToegangsbeheer) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('team.adminOnly')}</p>
            <Button variant="outline" onClick={() => navigate('/employer/cockpit')} className="mt-4">
              {t('team.backToCockpit')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const tabKnop = (sleutel: Weergave, Icon: React.ElementType, label: string) => (
    <button
      type="button"
      onClick={() => setWeergave(sleutel)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        weergave === sleutel ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          {tabKnop('boom', Network, t('team.weergaveBoom'))}
          {tabKnop('tabel', Table2, t('team.weergaveTabel'))}
        </div>
        {eigenVestiging && (
          <p className="text-xs text-gray-500">{t('team.vestigingsmanagerUitleg', { vestiging: locations[0]?.naam || '' })}</p>
        )}
        <Button onClick={() => setShowInviteModal(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          {t('team.inviteUser')}
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="py-12 text-center text-gray-500">{t('team.loading')}</div>
        ) : weergave === 'boom' ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">{t('team.boomUitleg')}</p>
            <ToegangsBoom
              members={members}
              locations={locations}
              departments={departments}
              organisatieNaam={overzicht?.organisatie?.naam || t('team.boomOrganisatie')}
              // AVG: een Vestigingsmanager ziet alleen de eigen vestiging, niet de concerntop
              hoofdgebruikerNaam={eigenVestiging ? null : overzicht?.organisatie?.hoofdgebruiker || null}
              wortelBewerkbaar={!eigenVestiging}
              onEdit={openWijziging}
              onMove={verplaats}
            />
            {members.length === 0 && (
              <div className="text-center pt-4">
                <Button variant="outline" onClick={() => setShowInviteModal(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('team.inviteFirst')}
                </Button>
              </div>
            )}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('team.empty')}</h2>
            <p className="text-gray-600 mb-6">
              {t('team.emptyDesc')}
            </p>
            <Button onClick={() => setShowInviteModal(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              {t('team.inviteFirst')}
            </Button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <select
                value={filter.location_id}
                onChange={(e) => setFilter((p) => ({ ...p, location_id: e.target.value, department_id: '' }))}
                className={selectKlasse}
                aria-label={t('team.filterVestiging')}
              >
                <option value="">{t('team.filterVestiging')}</option>
                <option value="geen">{t('team.scopeAlleVestigingen')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.naam}</option>
                ))}
              </select>
              <select
                value={filter.department_id}
                onChange={(e) => setFilter((p) => ({ ...p, department_id: e.target.value }))}
                className={selectKlasse}
                aria-label={t('team.filterAfdeling')}
              >
                <option value="">{t('team.filterAfdeling')}</option>
                <option value="geen">{t('team.scopeAlleAfdelingenOptie')}</option>
                {filterDepartments.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.naam}{dep.location_naam ? ` (${dep.location_naam})` : ''}
                  </option>
                ))}
              </select>
              <select
                value={filter.toegangsniveau}
                onChange={(e) => setFilter((p) => ({ ...p, toegangsniveau: e.target.value }))}
                className={selectKlasse}
                aria-label={t('team.filterNiveau')}
              >
                <option value="">{t('team.filterNiveau')}</option>
                {TOEGANGSNIVEAUS.map((niveau) => (
                  <option key={niveau} value={niveau}>{t(`team.niveau_${niveau}`)}</option>
                ))}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colName')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colEmail')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colFunctie')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colNiveau')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colRole')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colVestiging')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colAfdeling')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('team.colStatus')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('team.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gefilterd.map((m) => {
                    const status = teamStatus(m);
                    return (
                      <tr key={m.employer_user_id} className={status === 'ingetrokken' ? 'bg-gray-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                              status === 'actief' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {`${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`.toUpperCase()}
                            </span>
                            <span className="text-sm font-medium text-gray-900">{m.first_name} {m.last_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{m.email}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{m.functietitel || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{t(`team.niveau_${m.toegangsniveau}`)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{rolLabel(m.team_role)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{m.location_naam || t('team.scopeAlleVestigingen')}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{m.department_naam || t('team.scopeAlleAfdelingenOptie')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_KLASSEN[status]}`}>
                            {t(`team.status_${status}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="inline-flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => openWijziging(m)} disabled={!m.bewerkbaar} title={m.bewerkbaar ? t('team.actieWijzig') : t('team.nietBewerkbaar')} aria-label={t('team.actieWijzig')}>
                              <Settings2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => wisselToegang(m)}
                              disabled={!m.bewerkbaar || statusMutation.isPending}
                              title={m.is_active ? t('team.actieIntrekken') : t('team.actieHeractiveren')}
                              aria-label={m.is_active ? t('team.actieIntrekken') : t('team.actieHeractiveren')}
                            >
                              {m.is_active ? <Ban className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => verwijder(m)}
                              disabled={!m.bewerkbaar || removeMutation.isPending}
                              title={t('team.actieVerwijder')}
                              aria-label={t('team.actieVerwijder')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {gefilterd.length === 0 && (
                <p className="py-8 text-center text-sm text-gray-500">{t('team.filterLeeg')}</p>
              )}
            </div>
          </div>
        )}
      </Card>

      {wijziging && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('team.wijzigTitel', { name: `${wijziging.member.first_name} ${wijziging.member.last_name}`.trim() })}
              </h2>
              <button
                onClick={() => setWijziging(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">{wijziging.member.email}</p>
            <form onSubmit={handleWijzigingSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldFunctie')}</label>
                <input
                  type="text"
                  maxLength={150}
                  value={wijziging.functietitel}
                  onChange={(e) => setWijziging((p) => (p ? { ...p, functietitel: e.target.value } : p))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('team.functiePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldRole')}</label>
                <select
                  value={wijziging.team_role}
                  onChange={(e) => setWijziging((p) => (p ? { ...p, team_role: e.target.value as EmployerTeamRole } : p))}
                  className={selectKlasse}
                >
                  {EMPLOYER_ROLE_PRESETS.map((preset) => (
                    <option
                      key={preset.role}
                      value={preset.role}
                      // Admin-menurol (financiën) kent alleen de hoofdgebruiker toe
                      disabled={preset.role === 'admin' && !isHoofdgebruiker && wijziging.member.team_role !== 'admin'}
                    >
                      {preset.label} — {preset.description}
                    </option>
                  ))}
                </select>
                {wijziging.team_role !== wijziging.member.team_role && (
                  <p className="text-xs text-amber-600 mt-1">{t('team.rolWisselUitleg')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('team.fieldNiveau')}</label>
                <div className="space-y-2">
                  {TOEGANGSNIVEAUS.map((niveau) => {
                    const toegestaan = niveauOpties.includes(niveau) || wijziging.toegangsniveau === niveau;
                    return (
                      <label
                        key={niveau}
                        className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                          !toegestaan ? 'opacity-50 cursor-not-allowed border-gray-200'
                            : wijziging.toegangsniveau === niveau ? 'border-blue-500 bg-blue-50 cursor-pointer' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={wijziging.toegangsniveau === niveau}
                          disabled={!toegestaan}
                          onChange={() => setWijziging((p) => (p ? metNiveau(p, niveau) : p))}
                          className="mt-1 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{t(`team.niveau_${niveau}`)}</span>
                          <span className="block text-xs text-gray-600">{t(`team.niveauDesc_${niveau}`)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('team.scopeVestiging')}</label>
                  <select
                    value={wijziging.location_id}
                    disabled={wijziging.toegangsniveau === 'global_admin' || !!eigenVestiging}
                    onChange={(e) => {
                      const locationId = e.target.value;
                      setWijziging((p) => {
                        if (!p) return p;
                        // Exclusieve afdeling van andere vestiging deselecteren
                        const dep = departments.find((d) => d.id === p.department_id);
                        const departmentOngeldig = dep?.location_id && dep.location_id !== locationId && locationId !== '';
                        return { ...p, location_id: locationId, department_id: departmentOngeldig ? '' : p.department_id };
                      });
                    }}
                    className={selectKlasse}
                  >
                    <option value="">{t('team.scopeAlleVestigingen')}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.naam}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('team.scopeAfdeling')}</label>
                  <select
                    value={wijziging.department_id}
                    disabled={wijziging.toegangsniveau !== 'afdelingschef'}
                    onChange={(e) => setWijziging((p) => (p ? { ...p, department_id: e.target.value } : p))}
                    className={selectKlasse}
                  >
                    <option value="">{t('team.scopeAlleAfdelingenOptie')}</option>
                    {wijzigingDepartments.map((dep) => (
                      <option key={dep.id} value={dep.id}>
                        {dep.naam}{dep.location_naam ? ` (${dep.location_naam})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500">{t('team.editScopeDesc', { name: `${wijziging.member.first_name} ${wijziging.member.last_name}`.trim() })}</p>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <Button type="button" variant="outline" onClick={() => setWijziging(null)}>
                  {t('team.cancel')}
                </Button>
                <Button type="submit" loading={wijzigMutation.isPending}>
                  {t('team.saveScope')}
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      wisselToegang(wijziging.member);
                      setWijziging(null);
                    }}
                  >
                    {wijziging.member.is_active ? <Ban className="w-4 h-4 mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                    {wijziging.member.is_active ? t('team.actieIntrekken') : t('team.actieHeractiveren')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      verwijder(wijziging.member);
                      setWijziging(null);
                    }}
                    title={t('team.actieVerwijder')}
                    aria-label={t('team.actieVerwijder')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </form>
          </Card>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('team.modalTitle')}</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('team.modalDesc')}
            </p>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldEmail')}</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('team.emailPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldFirstName')}</label>
                <input
                  type="text"
                  required
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm((p) => ({ ...p, first_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldLastName')}</label>
                <input
                  type="text"
                  required
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm((p) => ({ ...p, last_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('team.fieldFunctie')}</label>
                <input
                  type="text"
                  maxLength={150}
                  value={inviteForm.functietitel}
                  onChange={(e) => setInviteForm((p) => ({ ...p, functietitel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('team.functiePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('team.fieldRole', 'Rol')}</label>
                <div className="space-y-2">
                  {EMPLOYER_ROLE_PRESETS.map((preset) => (
                    <label
                      key={preset.role}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        inviteForm.team_role === preset.role
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="team_role"
                        checked={inviteForm.team_role === preset.role}
                        // Admin-menurol (financiën) kent alleen de hoofdgebruiker toe
                        disabled={preset.role === 'admin' && !isHoofdgebruiker}
                        onChange={() => applyRolePreset(preset.role)}
                        className="mt-1 border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">{preset.label}</span>
                        <span className="block text-xs text-gray-600">{preset.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('team.fieldNiveau')}</label>
                <div className="space-y-2">
                  {TOEGANGSNIVEAUS.map((niveau) => {
                    const toegestaan = niveauOpties.includes(niveau) || inviteForm.toegangsniveau === niveau;
                    return (
                      <label
                        key={niveau}
                        className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                          !toegestaan ? 'opacity-50 cursor-not-allowed border-gray-200'
                            : inviteForm.toegangsniveau === niveau ? 'border-blue-500 bg-blue-50 cursor-pointer' : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={inviteForm.toegangsniveau === niveau}
                          disabled={!toegestaan}
                          onChange={() => setInviteForm((p) => (p ? metNiveau(p, niveau) : p))}
                          className="mt-1 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{t(`team.niveau_${niveau}`)}</span>
                          <span className="block text-xs text-gray-600">{t(`team.niveauDesc_${niveau}`)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('team.fieldScope', 'Toegangs-Scope')}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t(
                    'team.scopeUitleg',
                    'Beperk wat deze collega ziet tot één vestiging en/of afdeling. Zonder beperking geldt de universele scope (alle vestigingen en afdelingen).'
                  )}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('team.scopeVestiging', 'Gekoppeld aan vestiging')}</label>
                    <select
                      value={inviteForm.location_id}
                      disabled={inviteForm.toegangsniveau === 'global_admin' || !!eigenVestiging}
                      onChange={(e) => {
                        const locationId = e.target.value;
                        setInviteForm((p) => {
                          // Exclusieve afdeling van andere vestiging deselecteren
                          const dep = departments.find((d) => d.id === p.department_id);
                          const departmentOngeldig = dep?.location_id && dep.location_id !== locationId && locationId !== '';
                          return { ...p, location_id: locationId, department_id: departmentOngeldig ? '' : p.department_id };
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">{t('team.scopeAlleVestigingen', 'Alle vestigingen')}</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.naam}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('team.scopeAfdeling', 'Gekoppeld aan afdeling')}</label>
                    <select
                      value={inviteForm.department_id}
                      disabled={inviteForm.toegangsniveau !== 'afdelingschef'}
                      onChange={(e) => setInviteForm((p) => ({ ...p, department_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">{t('team.scopeAlleAfdelingenOptie', 'Alle afdelingen')}</option>
                      {selectableDepartments.map((dep) => (
                        <option key={dep.id} value={dep.id}>
                          {dep.naam}{dep.location_naam ? ` (${dep.location_naam})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {locations.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {t('team.scopeGeenVestigingen', 'Nog geen vestigingen aangemaakt — beheer deze in het Organisatieprofiel (tab 1).')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('team.fieldPermissions')}</label>
                <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {EMPLOYER_PERMISSION_OPTIONS.map(({ key, label }) => {
                    // Financiën/contracten zijn hard vergrendeld op de Admin-rol (RBAC)
                    const adminOnly = key === 'facturen' || key === 'toegang_cockpit';
                    const locked = adminOnly && inviteForm.team_role !== 'admin';
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-2 p-1 rounded ${
                          locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!locked && inviteForm.permissions.includes(key)}
                          disabled={locked}
                          onChange={() => togglePermission(key)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">
                          {label}
                          {locked && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({t('team.adminOnlyPermission', 'alleen Admin')})
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                  {t('team.cancel')}
                </Button>
                <Button type="submit" loading={inviteMutation.isPending}>
                  <Mail className="w-4 h-4 mr-2" />
                  {t('team.sendInvite')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};
